/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable */
import { loadConfig } from "config"
import React from "react"
import ReactDOM from "react-dom"
import "tippy.js/dist/tippy.css"
import App from "./App"
import {
  applyWebviewBodyClass,
  applyEcomPageBodyClass,
  applyEcomFullBleedBodyClass,
} from "utils/webview"
import { isEcomPath, isEcomFullBleedPath } from "constants/routes"
import { PromiseStatus } from "types/common.type"
import {
  MonitorErrorBoundary,
  monitorGlobalErrors,
} from "@grab-food/web-monitoring-helpers"
import {
  initializeScribeSDK,
  getGrabXValue,
  GrabXDataTypes,
  MerchantPortalFeatureFlag,
} from "grabx"
import {
  DEFAULT_DATADOG_RUM_CONFIG,
  configureFetchLifecycleRum,
} from "constants/datadog"
import { initDatadog } from "utils/datadog"
import { CrashFallback } from "components/Error/CrashFallback"

// Initialize MSW with auto-attach UI (dev-only)
if (process.env.NODE_ENV === "development") {
  import("./mocks/setupDevMsw").then(({ startDevMsw }) => {
    startDevMsw().then(() => {
      console.log("MSW initialized with project handlers and dev UI")
    })
  })
}

export default async function init(mount = document.getElementById("root")) {
  if (!Promise.allSettled) {
    Promise.allSettled = promises =>
      Promise.all(
        promises.map((promise, _) =>
          promise
            .then(value => ({
              status: PromiseStatus.Fulfilled,
              value,
            }))
            .catch(reason => ({
              status: PromiseStatus.Rejected,
              reason,
            })),
        ),
      )
  }

  // Tag <body> with `webview-mode` early so the webview layout (hidden sidebar +
  // full-width content) applies before first paint, with no flash.
  applyWebviewBodyClass()
  // Tag <body> with `ecom-page` on the initial eCom URL so the content-width floor is
  // released before first paint (NavigationContainer keeps it in sync on navigation).
  applyEcomPageBodyClass(isEcomPath(window.location.pathname))
  // Same, for the full-bleed content-width cap on the product wizard routes.
  applyEcomFullBleedBodyClass(isEcomFullBleedPath(window.location.pathname))

  initializeScribeSDK()
  const config = await loadConfig()
  window.config = config

  const rumConfig = await getGrabXValue(
    MerchantPortalFeatureFlag.portalDatadogRumConfig,
    GrabXDataTypes.Json,
    DEFAULT_DATADOG_RUM_CONFIG,
  )

  configureFetchLifecycleRum(rumConfig)

  if (process.env.NODE_ENV === "production") {
    initDatadog(window.config.datadog, rumConfig)

    monitorGlobalErrors()
  }
  // Root boundary per the BTP Web Frontend Monitoring Standard: no `resetKeys`,
  // since a P1 crash here means the whole app shell (sidebar/navigation included)
  // is gone, and auto-recovery could mask a real problem — the merchant's only
  // way out is `CrashFallback`'s hard reload.
  ReactDOM.render(
    <MonitorErrorBoundary
      severity="critical"
      boundaryName="root"
      fallback={<CrashFallback variant="critical" />}
    >
      <App />
    </MonitorErrorBoundary>,
    mount,
  )
  return () => ReactDOM.unmountComponentAtNode(mount)
}
